// ================================================================
// API Service - Axios wrapper for TimeTrack backend
// frontend/src/services/api.js
// v5.1 - Added Projects, Tasks, Activities, Roles APIs
// ================================================================

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('tt_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 - redirect to login
api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            localStorage.removeItem('tt_token');
            localStorage.removeItem('tt_user');
            window.location.href = '/login';
        }
        return Promise.reject(err);
    }
);

// ── Auth ─────────────────────────────────────────────────────

export const authApi = {
    login: (username, password) =>
        api.post('/auth/login', { username, password }),
};

// ── Timesheets (Employee) ────────────────────────────────────

export const timesheetApi = {
    getWeek: (date) =>
        api.get('/timesheets/week', { params: { date } }),

    save: (weekStartDate, entries) =>
        api.post('/timesheets', { weekStartDate, entries, action: 'SAVE' }),

    submit: (weekStartDate, entries) =>
        api.post('/timesheets', { weekStartDate, entries, action: 'SUBMIT' }),
};

// ── Projects ────────────────────────────────────────────────

export const projectApi = {
    /** GET /projects - all active projects (any user) */
    list: () => api.get('/projects'),

    /** GET /projects/active-only - only ACTIVE status projects (for timesheet) */
    activeOnly: () => api.get('/projects/active-only'),

    /** GET /projects/my - projects for current user's team */
    getMyProjects: () => api.get('/projects/my'),

    /** GET /projects/:id */
    get: (id) => api.get(`/projects/${id}`),

    /** POST /projects - create (Manager, Admin) */
    create: (data) => api.post('/projects', data),

    /** PUT /projects/:id - update (Manager who owns it, Admin) */
    update: (id, data) => api.put(`/projects/${id}`, data),

    /** DELETE /projects/:id - deactivate */
    deactivate: (id) => api.delete(`/projects/${id}`),
};

// ── Tasks ───────────────────────────────────────────────────

export const taskApi = {
    /** GET /tasks/my - tasks assigned to current user */
    getMyTasks: () => api.get('/tasks/my'),

    /** GET /tasks/team - team tasks (Manager, Team Lead) */
    getTeamTasks: () => api.get('/tasks/team'),

    /** GET /tasks/project/:projectId */
    getProjectTasks: (projectId) => api.get(`/tasks/project/${projectId}`),

    /** GET /tasks/:id */
    get: (id) => api.get(`/tasks/${id}`),

    /** POST /tasks - create (Team Lead, Manager, Admin) */
    create: (data) => api.post('/tasks', data),

    /** PUT /tasks/:id - update */
    update: (id, data) => api.put(`/tasks/${id}`, data),

    /** POST /tasks/:id/comments */
    addComment: (taskId, comment) => api.post(`/tasks/${taskId}/comments`, { comment }),

    /** POST /tasks/:id/time-log */
    logTime: (taskId, hours, notes) => api.post(`/tasks/${taskId}/time-log`, { hours, notes }),
};

// ── Activities ──────────────────────────────────────────────

export const activityApi = {
    /** GET /activities - all active activities (any user) */
    list: () => api.get('/activities'),

    /** GET /activities/category/:cat */
    getByCategory: (category) => api.get(`/activities/category/${category}`),

    /** POST /activities - create (Admin only) */
    create: (data) => api.post('/activities', data),

    /** PUT /activities/:id - update (Admin only) */
    update: (id, data) => api.put(`/activities/${id}`, data),

    /** DELETE /activities/:id - deactivate (Admin only) */
    deactivate: (id) => api.delete(`/activities/${id}`),
};

// ── Roles (Admin only) ──────────────────────────────────────

export const roleApi = {
    list: () => api.get('/admin/roles'),
    get: (id) => api.get(`/admin/roles/${id}`),
    create: (data) => api.post('/admin/roles', data),
    update: (id, data) => api.put(`/admin/roles/${id}`, data),
    deactivate: (id) => api.delete(`/admin/roles/${id}`),
};

// ── Admin: Employees ─────────────────────────────────────────

export const adminEmployeeApi = {
    list: (includeInactive = false) =>
        api.get('/admin/employees', { params: { includeInactive } }),
    get: (id) => api.get(`/admin/employees/${id}`),
    create: (data) => api.post('/admin/employees', data),
    update: (id, data) => api.put(`/admin/employees/${id}`, data),
    deactivate: (id) => api.delete(`/admin/employees/${id}`),
    reactivate: (id) => api.put(`/admin/employees/${id}/reactivate`),
};

// ── Admin: Timesheets ────────────────────────────────────────

export const adminTimesheetApi = {
    getForEmployee: (empId, date) =>
        api.get('/admin/timesheets', { params: { empId, date } }),
    edit: (employeeId, weekStartDate, entries, notes) =>
        api.put('/admin/timesheets', { employeeId, weekStartDate, entries, notes }),
    getAuditLog: () => api.get('/admin/audit'),
};

// ── Manager: Approvals ──────────────────────────────────────

export const managerApi = {
    getPending: () => api.get('/manager/pending'),
    getTeam: () => api.get('/manager/team'),
    getTeamMemberTimesheet: (empId, date) =>
        api.get('/manager/timesheets', { params: { empId, date } }),
    approve: (timesheetId, comments) =>
        api.put('/manager/approve', { timesheetId, comments }),
    reject: (timesheetId, comments) =>
        api.put('/manager/reject', { timesheetId, comments }),
    editTimesheet: (employeeId, weekStartDate, entries, notes, action) =>
        api.put('/manager/edit-timesheet', { employeeId, weekStartDate, entries, notes, action: action || 'SUBMIT' }),
};

// ── Lookups ──────────────────────────────────────────────────

export const lookupApi = {
    taskgroups: () => api.get('/lookup/taskgroups'),
    trc: () => api.get('/lookup/trc'),
    businessUnits: () => api.get('/lookup/business-units'),
    hrDepartments: () => api.get('/lookup/hr-departments'),
};

// ── Leave Management ────────────────────────────────────────

export const leaveApi = {
    getTypes: () => api.get('/leave/types'),
    getBalances: () => api.get('/leave/balances'),
    getMyLeaves: () => api.get('/leave/my'),
    submit: (data) => api.post('/leave/submit', data),
    cancel: (id) => api.put(`/leave/${id}/cancel`),
    getPendingManager: () => api.get('/leave/pending/manager'),
    getPendingHR: () => api.get('/leave/pending/hr'),
    getAllPending: () => api.get('/leave/pending/all'),
    getManagerHistory: () => api.get('/leave/history/manager'),
    getHRHistory: () => api.get('/leave/history/hr'),
    getAllHistory: () => api.get('/leave/history/all'),
    managerApprove: (id, comments) => api.put(`/leave/${id}/manager-approve`, { comments }),
    managerReject: (id, comments) => api.put(`/leave/${id}/manager-reject`, { comments }),
    hrApprove: (id, comments) => api.put(`/leave/${id}/hr-approve`, { comments }),
    hrReject: (id, comments) => api.put(`/leave/${id}/hr-reject`, { comments }),
};

export default api;
